/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * This is the starting point of your application.
 * oneAudi OS and Feature Hub Apps will use this file to bootstrap the app.
 */
import React from 'react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { Headline, ProgressIndicator, Text } from '@oneaudi/unified-web-components';
import { useQuery } from '@apollo/client';
import { useI18n } from '@oneaudi/i18n-context';
import styled from 'styled-components';
import { getDealerQueryVariables, queryString } from '../queryHook';
import { type ConfigProps, PartnerBusinessCard } from './components/PartnerBusinessCard';
import { mapContent } from './components/headless/ContentInterfaces';
import FeatureServiceContext from './FeatureServiceContext';

import { Container } from './style';
import { pruneOpeningHours } from '../lib/use-partner-data';
import { messages } from '../i18n/messages';
import { toISO3CountryCodeDealerFormatter } from '../lib/2toIso3';
import { layoutStyleStrings } from '../constants';

const StyledProgressIndicatorContainer = styled.div`
  display: flex;
  justify-content: center;
`;

interface FeatureAppProps {
  config?: Config;
  initialContent?: any;
  initialDealerData?: any;
}

interface Config extends Omit<ConfigProps, 'partnerID'> {
  [x: string]: any;
  partnerID?: string;
  variantConfig?: any;
  showVariant?: any;
}

interface PBCProps {
  config?: Config;
  initialDealerData?: any;
}

const PBCWrapper: React.FC<PBCProps> = ({ config, initialDealerData }: PBCProps) => {
  const context = React.useContext(FeatureServiceContext);
  const logger = context.logger || console;
  const language = context.localeService?.language || (config?.locale as string);
  const pbcConfig = React.useMemo<ConfigProps | undefined>(() => {
    if (!config?.partnerID) {
      logger.error(
        'Neither a content id to fetch partnerID from content or a config with partnerID is specified! Component will not render!',
      );
      return undefined;
    }

    return {
      ...config,
      variantConfig: {
        ...config?.variantConfig,
      },
      partnerID: config.partnerID,
    };
  }, [config, logger]);
  const missingPartnerDataErrorHeader = useI18n(messages.missingPartnerDataErrorHeader);
  const missingPartnerDataErrorMessage = useI18n(messages.missingPartnerDataErrorMessage);

  const regExp = /[a-zA-Z]/g;
  const isPartnerIdComplete = regExp.test(pbcConfig?.partnerID as string);
  const partnerId = isPartnerIdComplete
    ? pbcConfig?.partnerID
    : toISO3CountryCodeDealerFormatter(config?.locale as string, pbcConfig?.partnerID as string);

  const useDirectVtpDealer =
    Boolean(pbcConfig?.vtpDealer?.id) &&
    pbcConfig?.showVariant === layoutStyleStrings.dealerAndAddress;

  const { loading, data } = useQuery(queryString, {
    variables: getDealerQueryVariables(partnerId, language),
    fetchPolicy: 'cache-first',
    skip: !partnerId || useDirectVtpDealer || Boolean(initialDealerData),
  });
  let dealerData: any;

  if (useDirectVtpDealer) {
    dealerData = pbcConfig.vtpDealer;
    return <PartnerBusinessCard config={pbcConfig} data={dealerData} />;
  }

  if (pbcConfig && initialDealerData) {
    dealerData = initialDealerData;

    if (dealerData?.openingHours) {
      dealerData = {
        ...dealerData,
        openingHours: pruneOpeningHours(dealerData.openingHours, logger),
      };
    }

    return <PartnerBusinessCard config={pbcConfig} data={dealerData} />;
  }

  if (loading && !pbcConfig?.vtpDealer?.id) {
    return (
      <StyledProgressIndicatorContainer data-testid="pbc-loading">
        <ProgressIndicator
          data-testid="pbc-loading-indicator"
          variant="spinner"
          colorScheme="idle"
          type="infinite"
          size="lg"
        />
      </StyledProgressIndicatorContainer>
    );
  }

  if (data) {
    let { dealerById } = data;
    if (dealerById?.openingHours) {
      dealerById = {
        ...dealerById,
        openingHours: pruneOpeningHours(dealerById.openingHours, logger),
      };
      dealerData = dealerById;
    }
    dealerData = dealerById;

    return <PartnerBusinessCard config={pbcConfig} data={dealerData} />;
  }

  if (!data && !pbcConfig?.vtpDealer?.id && !loading) {
    console.error('Partner data does not exist.');
    return (
      <div data-testid="pbc-error" style={{ textAlign: 'left' }}>
        <Headline
          tag="div"
          variant={{ type: 'headline', order: '3', weight: 'normal' }}
          margin={[0, 0, 'var(--spacing-relative-2xs)', 0]}
          data-testid="pbc-error-heading"
        >
          {missingPartnerDataErrorHeader}
        </Headline>
        <Text
          data-testid="pbc-error-message"
          variant={{ type: 'copy', order: '1', style: 'normal' }}
        >
          {missingPartnerDataErrorMessage}
        </Text>
      </div>
    );
  }

  return null;
};

const FeatureApp: React.FC<FeatureAppProps> = ({
  config,
  initialContent,
  initialDealerData,
}: FeatureAppProps) => {
  const context = React.useContext(FeatureServiceContext);
  const { contentService } = context;

  const [rawContent, setRawContent] = React.useState(
    initialContent || contentService?.getContent(),
  );
  let content: any = null;

  React.useEffect(() => {
    const listener = () => {
      setRawContent(contentService?.getContent());
    };
    contentService?.onContentChange(listener);

    return () => {
      contentService?.removeOnContentChange(listener);
    };
  }, [contentService]);

  content = mapContent(rawContent);

  const pbcConfig = {
    partnerID: config?.partnerID ? config?.partnerID : content?.partnerID,
    variantConfig: config?.variantConfig
      ? config?.variantConfig
      : content?.variantConfigHeadless || content?.variantConfig,
    showVariant: config?.showVariant
      ? config?.showVariant
      : content?.showVariant || content?.pbcShowVariant,
    locale: config?.locale && config?.locale,
    imprint: config?.imprint && config?.imprint,
    registrationNumber: config?.registrationNumber && config?.registrationNumber,
    isBevAgency: config?.isBevAgency,
    isBEVAgency: config?.isBEVAgency,
    isBEVAgencyDealer: config?.isBEVAgencyDealer,
    investorSharedVehicle: config?.investorSharedVehicle && config?.investorSharedVehicle,
    nationWideSelling: config?.nationWideSelling,
    dealers: config?.dealers && config?.dealers,
    vtpDealerResultsURL: config?.vtpDealerResultsURL,
    vtpDealer: config?.vehicleBasic && config?.vehicleBasic?.dealer,
    dynamicLabel: config?.dynamicLabel && config?.dynamicLabel,
    removePadding: config?.removePadding || false,
    headline: config?.headline ? config?.headline : content?.headline,
    vtpPbcUrl: config?.vtpPbcUrl && config?.vtpPbcUrl,
    vtpDealerSearchURL: config?.vtpDealerSearchURL && config?.vtpDealerSearchURL,
  };

  const standAlonePBC = !config?.partnerID || !content?.pbcPartnerID;

  return (
    <div data-testid="pbc-root">
      <Container
        variant={pbcConfig?.showVariant}
        standAlonePBC={standAlonePBC}
        removePadding={pbcConfig.removePadding}
      >
        <PBCWrapper config={pbcConfig} initialDealerData={initialDealerData} />
      </Container>
    </div>
  );
};

export default FeatureApp;
